import React, { useState, useEffect } from 'react';
import { Solar, Lunar, I18n } from 'lunar-javascript';
import { DateTime } from 'luxon';
import { solarTermsData, solarTermsMap } from '../utils/solarTermsData';

// Translation Maps
const PENGZU_GAN_MAP = {
  '甲': 'Open no granaries, wealth will be lost.',
  '乙': 'Plant no crops, they won\'t grow well.',
  '丙': 'Repair no kitchens, disasters will arise.',
  '丁': 'Shave no heads, boils will appear.',
  '戊': 'Accept no land, the owner is cold.',
  '己': 'Break no contracts, it\'s empty and lost.',
  '庚': 'Weave no fabric, the loom will break.',
  '辛': 'Make no sauce, the master tastes nothing.',
  '壬': 'Drain no water, difficult to prevent.',
  '癸': 'Argue no words, the strong are meaningless.'
};

const PENGZU_ZHI_MAP = {
  '子': 'Ask no fortunes, confusion results.',
  '丑': 'Take no medicine, the energy won\'t return.',
  '寅': 'Offer no sacrifices, ghosts won\'t taste.',
  '卯': 'Dig no wells, water won\'t spring.',
  '辰': 'Weep not, grief leads to death.',
  '巳': 'Travel not far, wealth will be lost.',
  '午': 'Roof not houses, the master changes.',
  '未': 'Take no medicine, poison enters intestines.',
  '申': 'Move no beds, ghosts enter the room.',
  '酉': 'Guests not gathered, new lord is seated.',
  '戌': 'Eat no dogs, ghosts sleep in bed.',
  '亥': 'Marry not, the bride\'s bed is empty.'
};

const DIRECTION_MAP = {
  'Qian': 'Northwest (Qian)',
  'Kun': 'Southwest (Kun)',
  'Gen': 'Northeast (Gen)',
  'Xun': 'Southeast (Xun)',
  'Kan': 'North (Kan)',
  'Li': 'South (Li)',
  'Zhen': 'East (Zhen)',
  'Dui': 'West (Dui)',
  'Zhong': 'Center'
};

const getSolarTerm = (dt) => {
  // Check current year and previous year because solar terms for Jan are often in previous year's data
  const yearsToCheck = [dt.year - 1, dt.year];
  
  for (const year of yearsToCheck) {
    const yearData = solarTermsData[year];
    if (!yearData) continue;

    for (const [termName, isoStr] of Object.entries(yearData)) {
      // Parse the ISO string (which is in CST +08:00) and convert to the calendar's current timezone
      const termDate = DateTime.fromISO(isoStr).setZone(dt.zoneName);
      
      if (dt.hasSame(termDate, 'day')) {
        return {
          name: termName,
          chineseName: solarTermsMap[termName],
          time: termDate.toFormat('HH:mm:ss'),
          dateTime: termDate
        };
      }
    }
  }
  return null;
};

export default function WannianliCalendar({ dateStr, language = 'en' }) {
  // Initialize with the provided date or today
  const initialDate = dateStr ? DateTime.fromISO(dateStr) : DateTime.now();
  
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [viewMonth, setViewMonth] = useState(initialDate.startOf('month'));
  const [info, setInfo] = useState(null);

  // Sync internal state when dateStr prop changes (e.g. from parent date picker)
  useEffect(() => {
    if (dateStr) {
      const newDt = DateTime.fromISO(dateStr);
      if (newDt.isValid) {
        setSelectedDate(newDt);
        // Only update view month if it's different to avoid jumping when just changing days in same month
        // But if the user picked a date from the picker, we probably want to jump to that month.
        setViewMonth(prev => {
            if (newDt.hasSame(prev, 'month') && newDt.hasSame(prev, 'year')) {
                return prev;
            }
            return newDt.startOf('month');
        });
      }
    }
  }, [dateStr]);

  // Update info whenever selectedDate changes
  useEffect(() => {
    // Set Language for Lunar Library
    I18n.setLanguage(language === 'en' ? 'en' : 'chs');

    const dt = selectedDate;
    const solar = Solar.fromYmd(dt.year, dt.month, dt.day);
    const lunar = Lunar.fromSolar(solar);

    // Helper to format Lunar Date
    let lunarMonthStr, lunarDayStr;
    if (language === 'en') {
        lunarMonthStr = `Lunar Month ${lunar.getMonth()}`;
        lunarDayStr = `Lunar Day ${lunar.getDay()}`;
        // Handle Leap Month
        if (lunar.getMonth() < 0) {
            lunarMonthStr = `Leap Lunar Month ${Math.abs(lunar.getMonth())}`;
        }
    } else {
        lunarMonthStr = lunar.getMonthInChinese() + "月";
        lunarDayStr = lunar.getDayInChinese();
    }

    // Helper for PengZu Translation
    let pengZuGan = lunar.getPengZuGan();
    let pengZuZhi = lunar.getPengZuZhi();
    
    if (language === 'en') {
        // Extract the Chinese Stem/Branch char to look up map
        // getPengZuGan() in 'en' returns "Stem + Chinese Text" e.g. "Jia..."
        // But we need the original Chinese Stem to map.
        // Actually, we can just get the Day Stem/Branch directly.
        // Let's use the index.
        const ganIndex = lunar.getDayGanIndex(); // 0-9
        const zhiIndex = lunar.getDayZhiIndex(); // 0-11
        
        const stems = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
        const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
        
        pengZuGan = PENGZU_GAN_MAP[stems[ganIndex]] || pengZuGan;
        pengZuZhi = PENGZU_ZHI_MAP[branches[zhiIndex]] || pengZuZhi;
    }

    // Helper for Directions
    const mapDirection = (dir) => (language === 'en' && DIRECTION_MAP[dir]) ? DIRECTION_MAP[dir] : dir;

    const solarTermInfo = getSolarTerm(dt);
    let solarTermDisplay = '';
    if (solarTermInfo) {
        solarTermDisplay = language === 'en' 
            ? `${solarTermInfo.name} ${solarTermInfo.time}`
            : `${solarTermInfo.chineseName} ${solarTermInfo.time}`;
    } else {
        // Fallback
        const jq = lunar.getJieQi();
        if (jq) solarTermDisplay = jq;
        else solarTermDisplay = (language === 'en' ? 'N/A' : '无');
    }

    const data = {
      gregorian: dt.toFormat('yyyy-MM-dd'),
      solarDay: dt.day,
      solarMonthYearWeek: language === 'en'
        ? dt.setLocale('en').toFormat('MMMM yyyy EEEE')
        : dt.setLocale('zh-CN').toFormat('yyyy年M月 EEEE'),
      lunar: {
        year: lunar.getYearInGanZhi() + (language === 'en' ? ' Year' : '年'),
        month: lunar.getMonthInGanZhi() + (language === 'en' ? ' Month' : '月'),
        day: lunar.getDayInGanZhi() + (language === 'en' ? ' Day' : '日'),
        dateStr: lunar.toString(),
        monthStr: lunarMonthStr,
        dayStr: lunarDayStr,
        zodiac: lunar.getYearShengXiao(),
      },
      solarTerm: solarTermDisplay,
      yi: lunar.getDayYi(),
      ji: lunar.getDayJi(),
      pengZu: {
        gan: pengZuGan,
        zhi: pengZuZhi
      },
      shenWei: {
        xi: mapDirection(lunar.getDayPositionXi()),
        yang: mapDirection(lunar.getDayPositionYangGui()),
        fu: mapDirection(lunar.getDayPositionFu()),
        cai: mapDirection(lunar.getDayPositionCai()),
      }
    };

    setInfo(data);
  }, [selectedDate, language]);

  // Calendar Grid Generation
  const generateCalendarDays = () => {
    const startOfMonth = viewMonth.startOf('month');
    const endOfMonth = viewMonth.endOf('month');
    const startDayOfWeek = startOfMonth.weekday; // 1 (Mon) - 7 (Sun)
    
    // Adjust for Sunday start if needed, but Luxon is Mon=1. Let's assume Mon start for now or adjust.
    // Let's do standard Mon-Sun grid.
    
    const days = [];
    
    // Pad previous month
    for (let i = 1; i < startDayOfWeek; i++) {
      const d = startOfMonth.minus({ days: startDayOfWeek - i });
      days.push({ 
          date: d, 
          isCurrentMonth: false,
          solarTerm: getSolarTerm(d)
      });
    }
    
    // Current month
    for (let i = 1; i <= endOfMonth.day; i++) {
      const d = startOfMonth.set({ day: i });
      days.push({ 
          date: d, 
          isCurrentMonth: true,
          solarTerm: getSolarTerm(d)
      });
    }
    
    // Pad next month to complete the row (optional, but looks better)
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const d = endOfMonth.plus({ days: i });
        days.push({ 
            date: d, 
            isCurrentMonth: false,
            solarTerm: getSolarTerm(d)
        });
      }
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    const newMonth = viewMonth.minus({ months: 1 });
    setViewMonth(newMonth);
    
    // Try to keep the same day, or snap to end of month
    const daysInNewMonth = newMonth.daysInMonth;
    const targetDay = Math.min(selectedDate.day, daysInNewMonth);
    setSelectedDate(newMonth.set({ day: targetDay }));
  };

  const handleNextMonth = () => {
    const newMonth = viewMonth.plus({ months: 1 });
    setViewMonth(newMonth);

    // Try to keep the same day, or snap to end of month
    const daysInNewMonth = newMonth.daysInMonth;
    const targetDay = Math.min(selectedDate.day, daysInNewMonth);
    setSelectedDate(newMonth.set({ day: targetDay }));
  };

  const handlePrevYear = () => {
    const newMonth = viewMonth.minus({ years: 1 });
    setViewMonth(newMonth);

    // Try to keep the same day, or snap to end of month
    const daysInNewMonth = newMonth.daysInMonth;
    const targetDay = Math.min(selectedDate.day, daysInNewMonth);
    setSelectedDate(newMonth.set({ day: targetDay }));
  };

  const handleNextYear = () => {
    const newMonth = viewMonth.plus({ years: 1 });
    setViewMonth(newMonth);

    // Try to keep the same day, or snap to end of month
    const daysInNewMonth = newMonth.daysInMonth;
    const targetDay = Math.min(selectedDate.day, daysInNewMonth);
    setSelectedDate(newMonth.set({ day: targetDay }));
  };
  const handleToday = () => {
    const now = DateTime.now();
    setSelectedDate(now);
    setViewMonth(now.startOf('month'));
  };

  if (!info) return <div>Loading...</div>;

  // Styles
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    fontFamily: "'Noto Serif SC', serif",
    color: '#333',
    backgroundColor: '#fffdf5',
    borderRadius: '8px',
    padding: '15px',
    height: '100%',
    overflow: 'hidden'
  };

  const contentWrapperStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    flex: 1,
    overflowY: 'auto'
  };

  const calendarSectionStyle = {
    flex: '1 1 300px',
    minWidth: '300px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '15px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  };

  const detailsSectionStyle = {
    flex: '1 1 300px',
    minWidth: '300px',
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderRadius: '8px',
    padding: '15px',
    border: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  };

  // Calendar Grid Styles
  const gridHeaderStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#666',
    fontSize: '0.9rem'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '5px'
  };

  const dayCellStyle = (isCurrentMonth, isSelected, isToday, hasSolarTerm) => ({
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '8px',
    backgroundColor: isSelected ? '#d4af37' : (isToday ? '#fff3cd' : 'transparent'),
    color: isSelected ? 'white' : (isCurrentMonth ? '#333' : '#ccc'),
    fontWeight: isSelected || isToday ? 'bold' : 'normal',
    fontSize: '0.9rem',
    border: isToday && !isSelected ? '1px solid #d4af37' : 'none',
    transition: 'all 0.2s',
    position: 'relative' // For the dot
  });

  const solarTermDotStyle = {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#ff5722'
  };

  const weekDays = language === 'en' 
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['一', '二', '三', '四', '五', '六', '日'];

  const labelStyle = {
    fontWeight: 'bold',
    color: '#888',
    fontSize: '0.9rem',
    marginBottom: '5px',
    display: 'block'
  };

  const valueStyle = {
    fontSize: '1.1rem',
    color: '#2c3e50'
  };

  const yiJiStyle = {
    display: 'flex',
    gap: '10px',
    marginTop: '10px'
  };

  const yiStyle = {
    flex: 1,
    padding: '10px',
    backgroundColor: '#e8f5e9',
    borderRadius: '6px',
    borderLeft: '4px solid #4caf50'
  };

  const jiStyle = {
    flex: 1,
    padding: '10px',
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    borderLeft: '4px solid #f44336'
  };

  // Header Styles
  const headerStyle = {
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    marginBottom: '10px',
    padding: '0 5px'
  };

  const monthTitleStyle = {
    fontSize: '1.5rem', 
    fontWeight: '700', 
    color: '#2c3e50',
    fontFamily: "'Noto Serif SC', serif"
  };

  const controlsStyle = {
    display: 'flex', 
    gap: '8px',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: '4px',
    borderRadius: '30px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  };

  const navBtnStyle = {
    border: 'none', 
    background: 'transparent', 
    cursor: 'pointer', 
    fontSize: '1.1rem',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: '#555',
    transition: 'background 0.2s'
  };

  const todayBtnStyle = {
    padding: '6px 16px', 
    borderRadius: '20px', 
    border: 'none', 
    background: '#d4af37', 
    color: 'white', 
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    boxShadow: '0 2px 6px rgba(212, 175, 55, 0.2)'
  };

  return (
    <div style={containerStyle}>
      {/* Calendar Header Controls */}
      <div style={headerStyle}>
        <div style={monthTitleStyle}>
          {viewMonth.toFormat(language === 'en' ? 'MMMM yyyy' : 'yyyy年 M月')}
        </div>
        <div style={controlsStyle}>
            <button 
              onClick={handlePrevYear} 
              style={navBtnStyle}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={language === 'en' ? 'Previous Year' : '上一年'}
            >
              &lt;&lt;
            </button>
            <button 
              onClick={handlePrevMonth} 
              style={navBtnStyle}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={language === 'en' ? 'Previous Month' : '上一月'}
            >
              &lt;
            </button>
            <button 
              onClick={handleToday} 
              style={todayBtnStyle}
            >
              {language === 'en' ? 'Today' : '今天'}
            </button>
            <button 
              onClick={handleNextMonth} 
              style={navBtnStyle}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={language === 'en' ? 'Next Month' : '下一月'}
            >
              &gt;
            </button>
            <button 
              onClick={handleNextYear} 
              style={navBtnStyle}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={language === 'en' ? 'Next Year' : '下一年'}
            >
              &gt;&gt;
            </button>
        </div>
      </div>

      <div style={contentWrapperStyle}>
        {/* Left: Calendar Grid */}
        <div style={calendarSectionStyle}>
          <div style={gridHeaderStyle}>
            {weekDays.map(d => <div key={d}>{d}</div>)}
          </div>
          <div style={gridStyle}>
            {generateCalendarDays().map((item, idx) => {
                const isSelected = item.date.hasSame(selectedDate, 'day');
                const isToday = item.date.hasSame(DateTime.now(), 'day');
                // Get simple lunar day for grid
                const s = Solar.fromYmd(item.date.year, item.date.month, item.date.day);
                const l = Lunar.fromSolar(s);
                let lunarDayDisplay;
                if (language === 'en') {
                    // For English, just show the day number to keep it compact
                    // Or show "D1", "D2" etc.
                    // Special case for 1st day of month?
                    if (l.getDay() === 1) {
                        lunarDayDisplay = (l.getMonth() < 0 ? "Leap " : "") + "M" + Math.abs(l.getMonth());
                    } else {
                        lunarDayDisplay = "Day " + l.getDay();
                    }
                } else {
                    lunarDayDisplay = l.getDayInChinese();
                }
                
                return (
                    <div 
                        key={idx} 
                        style={dayCellStyle(item.isCurrentMonth, isSelected, isToday, !!item.solarTerm)}
                        onClick={() => setSelectedDate(item.date)}
                    >
                        {item.solarTerm && <div style={solarTermDotStyle} title={language === 'en' ? item.solarTerm.name : item.solarTerm.chineseName} />}
                        <span style={{ fontSize: '1.1rem', marginBottom: '2px' }}>{item.date.day}</span>
                        <span style={{ fontSize: '0.7rem', opacity: isSelected ? 0.9 : 0.6 }}>{lunarDayDisplay}</span>
                    </div>
                );
            })}
          </div>
          <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#666', display: 'flex', alignItems: 'center', gap: '5px' }}>
             <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ff5722' }}></div>
             <span>{language === 'en' ? 'Solar Term (Local Time)' : '节气 (当地时间)'}</span>
          </div>
        </div>

        {/* Right: Detailed Info */}
        <div style={detailsSectionStyle}>
            <div style={{ textAlign: 'center', borderBottom: '2px solid #d4af37', paddingBottom: '15px', marginBottom: '20px' }}>
                <div style={{ fontSize: '5rem', fontWeight: 'bold', color: '#d4af37', lineHeight: '1' }}>
                    {info.solarDay}
                </div>
                <div style={{ fontSize: '1.3rem', marginTop: '5px', color: '#555' }}>
                    {info.solarMonthYearWeek}
                </div>
                
                <div style={{ fontSize: '1.5rem', marginTop: '15px', fontWeight: 'bold', color: '#333' }}>
                {info.lunar.dayStr}
                </div>
                <div style={{ fontSize: '1.1rem', marginTop: '5px', color: '#666' }}>
                {info.lunar.monthStr} · {info.lunar.year} ({info.lunar.zodiac})
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', border: '1px solid #eee' }}>
                <span style={labelStyle}>{language === 'en' ? 'Solar Term' : '节气'}</span>
                <span style={valueStyle}>{info.solarTerm || (language === 'en' ? 'N/A' : '无')}</span>
                </div>
                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', border: '1px solid #eee' }}>
                <span style={labelStyle}>{language === 'en' ? 'Bazi Day' : '干支日'}</span>
                <span style={valueStyle}>{info.lunar.day}</span>
                </div>
            </div>

            <div style={yiJiStyle}>
                <div style={yiStyle}>
                <div style={{ color: '#2e7d32', fontWeight: 'bold', marginBottom: '5px' }}>
                    {language === 'en' ? 'Ausipicious (Yi)' : '宜'}
                </div>
                <div style={{ fontSize: '0.95rem' }}>
                    {info.yi.join(', ')}
                </div>
                </div>
                <div style={jiStyle}>
                <div style={{ color: '#c62828', fontWeight: 'bold', marginBottom: '5px' }}>
                    {language === 'en' ? 'Inauspicious (Ji)' : '忌'}
                </div>
                <div style={{ fontSize: '0.95rem' }}>
                    {info.ji.join(', ')}
                </div>
                </div>
            </div>

            <div style={{ marginTop: '20px', marginBottom: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', border: '1px solid #eee' }}>
                <span style={labelStyle}>{language === 'en' ? 'Deity Positions' : '神位'}</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
                <div><span style={{color:'#d4af37'}}>{language === 'en' ? 'Joy (Xi):' : '喜神:'}</span> {info.shenWei.xi}</div>
                <div><span style={{color:'#d4af37'}}>{language === 'en' ? 'Yang Noble:' : '阳贵:'}</span> {info.shenWei.yang}</div>
                <div><span style={{color:'#d4af37'}}>{language === 'en' ? 'Luck (Fu):' : '福神:'}</span> {info.shenWei.fu}</div>
                <div><span style={{color:'#d4af37'}}>{language === 'en' ? 'Wealth (Cai):' : '财神:'}</span> {info.shenWei.cai}</div>
                </div>
            </div>
            
            <div style={{ marginTop: '10px', marginBottom: '15px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px', border: '1px solid #eee' }}>
                <span style={labelStyle}>{language === 'en' ? 'Peng Zu' : '彭祖百忌'}</span>
                <div style={{ fontSize: '0.9rem' }}>
                    <div>{info.pengZu.gan}</div>
                    <div>{info.pengZu.zhi}</div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}
